import type { Diagram } from '@/types/diagram';
import { throwApiError } from '@/lib/errors';

const BACKEND_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

export interface DraftDiagramOut {
  id: number;
  project_id: number;
  external_id: string | null;
  name: string;
  description: string | null;
  payload: {
    name: string;
    description: string;
    diagram_schema_version: number;
    entities: Diagram['entities'];
    boundaries: Diagram['boundaries'];
    paths: Diagram['paths'];
  };
  source: string;
  created_by: string;
}

export const draftDiagramService = {
  list: async (projectId: number): Promise<DraftDiagramOut[]> => {
    const response = await fetch(`${BACKEND_BASE}/api/draft-diagrams/${projectId}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to load draft diagrams');
    }
    return response.json();
  },

  remove: async (draftId: number): Promise<void> => {
    const response = await fetch(`${BACKEND_BASE}/api/draft-diagrams/${draftId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to discard draft diagram');
    }
  },

  patchPayload: async (
    draftId: number,
    patch: { paths?: Diagram['paths']; entities?: Diagram['entities']; boundaries?: Diagram['boundaries'] },
  ): Promise<DraftDiagramOut> => {
    const response = await fetch(`${BACKEND_BASE}/api/draft-diagrams/${draftId}/payload`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update draft diagram');
    }
    return response.json();
  },

  importToThreatCat: async (
    draftId: number,
    cookie: string,
    projectVersionId: string,
  ): Promise<{ ok: boolean; name: string }> => {
    const response = await fetch(`${BACKEND_BASE}/api/draft-diagrams/${draftId}/import`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-ThreatCat-Cookie': cookie,
        'X-ThreatCat-Project-Version-Id': projectVersionId,
      },
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to import draft diagram to ThreatCat');
    }
    return response.json();
  },
};
